import React, { useContext, useState } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { COVERAGE_FORM, COVERAGE_NAMES } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import {
  CommissionSection,
  SectionTitle,
  BenchmarkSection,
} from "../../components";
import { TabPanel } from "../../../../components";
import { RFP_ACTION_TYPES } from "../../constants";
import { RfpContext } from "../../contexts/rfpContext";
import { scrollToTop } from "../../../../utils";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import envVariables from "../../../../projectEnvVariables";
import CommentsField from "../components/commentsField";

import { createTabbedFormValidationHandlers } from "./coveragesValidations";
// TODO(eslint): fix cycle
// eslint-disable-next-line import/no-cycle
import { isValidPlan } from "./coverages";

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

const {
  PLAN_TYPE,
  PLANS,
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
  CONTRIBUTION_TYPE,
  CONTRIBUTION_VALUE,
  CONTRIBUTION_STRUCTURE,
  CURRENT_COMMISSION,
  ENROLLED_LIVES,
  FUNDING_ARRANGEMENT,
  REQUESTED_COMMISSION,
  TOTAL_EMPLOYER_PAID,
  TOTAL_EMPLOYEE_PAID,
  CONTRIBUTORY,
  EMPLOYER_CONTRIBUTION_PERCENTAGE,
  EMPLOYER_CONTRIBUTION_AMOUNT,
  FULLY_FUNDED,
  ASO,
  DUAL_PLAN_TYPE,
  PPO1_PPO2,
  PPO_DHMO,
  DUAL_OPTION_PPO_1,
  DUAL_OPTION_PPO_2,
  DUAL_OPTION_PPO,
  SINGLE_OPTION_PPO,
  DUAL_OPTION_DHMO,
  DHMO,
  NO_PLAN,
  INDEMNITY,
  PPO,
} = COVERAGE_FORM;

const { DENTAL_PPO, DENTAL_DHMO } = COVERAGE_NAMES;

const initialValues = {
  [CONTRIBUTION_STRUCTURE]: "",
  [CONTRIBUTION_TYPE]: "",
  [CONTRIBUTION_VALUE]: "",
  [ENROLLED_LIVES]: "",
  [FUNDING_ARRANGEMENT]: "",
  [CURRENT_COMMISSION]: "",
  [REQUESTED_COMMISSION]: "",
  [ADDITIONAL_NOTES]: "",
  [ALTERNATE_PLANS]: "",
};

export const initialDentalCoveragesDetails = {
  [PLAN_TYPE]: PPO,
  [DUAL_PLAN_TYPE]: null,
  [PLANS]: [
    {
      ...initialValues,
      name: PPO,
    },
  ],
};

export const getInitialDentalCoveragesDetails = (type) => ({
  [PLAN_TYPE]: type === DENTAL_PPO ? SINGLE_OPTION_PPO : null,
  [DUAL_PLAN_TYPE]: null,
  [PLANS]: [
    {
      ...initialValues,
      name: type === DENTAL_PPO ? SINGLE_OPTION_PPO : null,
      displayName: type === DENTAL_DHMO ? NO_PLAN : undefined,
    },
  ],
});

// Falls back to the single Dental PDF until an environment sets the dual URL.
const getBenchmarksUrl = (coverage) => {
  const isDualOptionPpo = coverage.details[PLAN_TYPE] === DUAL_OPTION_PPO;

  return (
    (isDualOptionPpo &&
      envVariables.VITE_REACT_APP_DENTAL_DUAL_BENCHMARKS_URL) ||
    envVariables.VITE_REACT_APP_DENTAL_BENCHMARKS_URL ||
    "/"
  );
};

const isSinglePlanFn = (coverage) =>
  coverage.details[PLAN_TYPE] === SINGLE_OPTION_PPO ||
  coverage.name === DENTAL_DHMO;

const Dental = ({
  coverage,
  handleNext,
  isLastCoverage,
  nextCoverage,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);
  const [activeTab, setActiveTab] = useState(0);
  const isSinglePlan = isSinglePlanFn(coverage);

  const handleFormChange = (updatedCoverage, currentPlanKey, field) => {
    let fieldKey = field;
    if (fieldKey === PLAN_TYPE) {
      setActiveTab(0);
      fieldKey = undefined;
    }
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  const handleBackAction = () => {
    if (isSinglePlan || activeTab === 0) {
      handleBack();
    } else {
      setActiveTab(0);
    }
    scrollToTop();
  };

  const handleNextAction = () => {
    if (isSinglePlan || activeTab === 1) {
      handleNext();
    } else {
      setActiveTab(1);
    }
    scrollToTop();
  };

  const backButtonName =
    isSinglePlan || activeTab === 0
      ? prevCoverage?.name
      : coverage.details[DUAL_PLAN_TYPE] === PPO1_PPO2
        ? DUAL_OPTION_PPO_1
        : DUAL_OPTION_PPO;

  const nextButtonName =
    isSinglePlan || activeTab === 1
      ? nextCoverage?.name
      : coverage.details[DUAL_PLAN_TYPE] === PPO1_PPO2
        ? DUAL_OPTION_PPO_2
        : DUAL_OPTION_DHMO;

  const displayNextButton = isLastCoverage
    ? coverage.details[PLAN_TYPE] === DUAL_OPTION_PPO && activeTab === 0
    : true;

  const displayBackButton = isFirstCoverage
    ? coverage.details[PLAN_TYPE] === DUAL_OPTION_PPO && activeTab === 1
    : true;

  const isDisabledNextStep =
    isSinglePlan || activeTab === 1 ? !coverage?.isValid : false;

  return (
    <>
      <DentalForm
        coverage={coverage}
        onFormChange={handleFormChange}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
      />
      <CoverageButtonWrapper
        displayBackButton={displayBackButton}
        displayNextButton={displayNextButton}
        backButtonName={backButtonName}
        nextButtonName={nextButtonName}
        handleBackAction={handleBackAction}
        handleNextAction={handleNextAction}
        isDisabled={isDisabledNextStep}
        isBackDisabled={false}
      />
    </>
  );
};

export const DentalForm = ({
  coverage,
  onFormChange,
  activeTab,
  onChangeTab,
}) => {
  const isDHMO = coverage.name === DENTAL_DHMO;
  const isCurrentCoverage = coverage?.isCurrentCoverage;
  const currentPlan = coverage?.details[PLANS][activeTab];
  const currentPlanErrors = currentPlan?.errors || {};

  const isTabError = (tab) =>
    !isValidPlan(coverage?.details[PLANS][tab]?.errors);

  const firstTab = isSinglePlanFn(coverage)
    ? coverage.details[PLAN_TYPE]
    : coverage.details[DUAL_PLAN_TYPE] === PPO1_PPO2
      ? DUAL_OPTION_PPO_1
      : SINGLE_OPTION_PPO;
  const secondTab =
    coverage.details[DUAL_PLAN_TYPE] === PPO1_PPO2
      ? DUAL_OPTION_PPO_2
      : DUAL_OPTION_DHMO;

  const handleChangeTab = (_, newValue) => {
    if (onChangeTab) {
      onChangeTab(newValue);
    }
  };

  const handlePlanTypeChange = (e) => {
    const { value, name } = e.target;
    const plans =
      value === PPO ||
      value === DHMO ||
      value === INDEMNITY ||
      value === SINGLE_OPTION_PPO
        ? [{ ...initialValues, name: value }]
        : [
            { ...initialValues, name: DUAL_OPTION_PPO_1 },
            { ...initialValues, name: DUAL_OPTION_PPO_2 },
          ];

    const dualPlanType = value === DUAL_OPTION_PPO ? PPO1_PPO2 : null;

    const payload = {
      ...coverage,
      details: {
        [name]: value,
        [DUAL_PLAN_TYPE]: dualPlanType,
        [PLANS]: plans,
      },
    };

    onFormChange(payload, activeTab, name);
  };

  const handleNumericFieldChange = (target, planName) => {
    const regex = /^\d+$/;
    if (target.value == "" || regex.test(target.value)) {
      handlePlanFormChange(target, planName);
    }
  };

  const handlePlanFormChange = ({ name, value }, planName) => {
    const plans = isDHMO
      ? coverage.details.plans.map((plan) => ({
          ...plan,
          [CONTRIBUTION_VALUE]:
            name === CONTRIBUTION_TYPE ? "" : plan[CONTRIBUTION_VALUE],
          [name]: value,
        }))
      : coverage.details.plans.map((plan) => {
          if (plan.name === planName) {
            // reset contribution value field if contribution type is changed
            if (name === CONTRIBUTION_TYPE) {
              plan[CONTRIBUTION_VALUE] = "";
            }

            return {
              ...plan,
              [CONTRIBUTION_VALUE]:
                name === CONTRIBUTION_TYPE ? "" : plan[CONTRIBUTION_VALUE],
              [name]: value,
            };
          }
          return plan;
        });

    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: plans },
    };

    onFormChange(payload, activeTab, name);
  };

  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createTabbedFormValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab,
      isDHMO: currentPlan?.type === "DHMO",
    });

  const planForm = (planName) => (
    <>
      {isCurrentCoverage && (
        <>
          <SectionTitle>Enrolled Lives</SectionTitle>
          <Grid container spacing={2} px={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                autoComplete="off"
                fullWidth
                name={ENROLLED_LIVES}
                error={!!currentPlanErrors[ENROLLED_LIVES]}
                value={currentPlan[ENROLLED_LIVES] || ""}
                onChange={(e) => {
                  handleNumericFieldChange(e.target, planName);
                }}
                onFocus={() => {
                  handlePreviousFieldsValidation(ENROLLED_LIVES, planName);
                }}
                onBlur={(e) => {
                  handlePlanFormChangeWithValidation(e.target, planName);
                }}
                label={
                  <Typography>
                    Number of Enrolled Members{" "}
                    <span className={"required"}>*</span>
                  </Typography>
                }
              />
              {!!currentPlanErrors && (
                <Typography variant="caption" color="danger">
                  {currentPlanErrors[ENROLLED_LIVES]}
                </Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}

      <SectionTitle>Contribution Structure</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl
          onFocus={() => {
            handlePreviousFieldsValidation(CONTRIBUTION_STRUCTURE, planName);
          }}
        >
          <FormLabel id="contribution-structure-label" required>
            Select a Contribution Structure
          </FormLabel>
          <RadioGroup
            value={currentPlan[CONTRIBUTION_STRUCTURE]}
            row
            aria-labelledby="contribution-structure-label"
            name={CONTRIBUTION_STRUCTURE}
            onChange={(e) => {
              handlePlanFormChangeWithValidation(e.target, planName);
            }}
          >
            <FormControlLabel
              value={TOTAL_EMPLOYER_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYER_PAID}
            />
            <FormControlLabel
              value={TOTAL_EMPLOYEE_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYEE_PAID}
            />
            <FormControlLabel
              value={CONTRIBUTORY}
              control={<Radio />}
              label={CONTRIBUTORY}
            />
          </RadioGroup>
        </FormControl>
        {currentPlan[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY && (
          <Box mt={2}>
            <FormControl
              onFocus={() => {
                handlePreviousFieldsValidation(CONTRIBUTION_TYPE, planName);
              }}
            >
              <FormLabel id="contribution-amount-label">
                Add Contribution amount
              </FormLabel>
              <Box mt={1}>
                <RadioGroup
                  value={currentPlan[CONTRIBUTION_TYPE]}
                  aria-labelledby="contribution-amount-label"
                  name={CONTRIBUTION_TYPE}
                  onChange={(e) => {
                    handlePlanFormChangeWithValidation(e.target, planName);
                  }}
                >
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_PERCENTAGE}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_PERCENTAGE}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_PERCENTAGE && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE || ""]}
                            onChange={(e) => {
                              handlePlanFormChange(e.target, planName);
                            }}
                            onFocus={() => {
                              handlePreviousFieldsValidation(
                                CONTRIBUTION_VALUE,
                                planName,
                              );
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(
                                e.target,
                                planName,
                              );
                            }}
                            label={<Typography>% percentage</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_AMOUNT}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_AMOUNT}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_AMOUNT && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE || ""]}
                            onChange={(e) => {
                              handlePlanFormChange(e.target, planName);
                            }}
                            onFocus={() => {
                              handlePreviousFieldsValidation(
                                CONTRIBUTION_VALUE,
                                planName,
                              );
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(
                                e.target,
                                planName,
                              );
                            }}
                            label={<Typography>$ amount</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>
        )}
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[CONTRIBUTION_STRUCTURE]}
            </Typography>
          )}
        </Box>
      </Box>

      {!(
        coverage.details[PLAN_TYPE] === DHMO ||
        (coverage.details[PLAN_TYPE] === DUAL_OPTION_PPO &&
          coverage.details[DUAL_PLAN_TYPE] === PPO_DHMO &&
          planName === DUAL_OPTION_DHMO)
      ) && (
        <>
          <SectionTitle>Funding Arrangement</SectionTitle>
          <Box pl={2} pr={2}>
            <FormControl
              onFocus={() => {
                handlePreviousFieldsValidation(FUNDING_ARRANGEMENT, planName);
              }}
            >
              <FormLabel id="funding-arrangement-label" required>
                Select a Funding Arrangement
              </FormLabel>
              <RadioGroup
                value={currentPlan[FUNDING_ARRANGEMENT]}
                row
                aria-labelledby="funding-arrangement-label"
                name={FUNDING_ARRANGEMENT}
                onChange={(e) => {
                  handlePlanFormChangeWithValidation(e.target, planName);
                }}
              >
                <FormControlLabel
                  value={FULLY_FUNDED}
                  control={<Radio />}
                  label={FULLY_FUNDED}
                />
                <FormControlLabel value={ASO} control={<Radio />} label={ASO} />
              </RadioGroup>
            </FormControl>
            <Box>
              {!!currentPlanErrors && (
                <Typography variant="caption" color="danger">
                  {currentPlanErrors[FUNDING_ARRANGEMENT]}
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        planName={planName}
        isCurrentCoverage={isCurrentCoverage}
      />

      {!isCurrentCoverage && (
        <BenchmarkSection
          handlePlanFormChange={handlePlanFormChange}
          currentPlan={currentPlan}
          planName={planName}
          link={getBenchmarksUrl(coverage)}
          coverageName={coverage?.name}
        />
      )}

      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target, planName);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );

  return (
    <>
      {!isDHMO && (
        <>
          <SectionTitle>Plan Type</SectionTitle>
          <Box pl={2} pr={2}>
            <FormControl>
              <FormLabel id="plan-type-label" required>
                Select a Plan Type
              </FormLabel>
              <RadioGroup
                value={coverage.details[PLAN_TYPE]}
                row
                aria-labelledby="plan-type-label"
                name={PLAN_TYPE}
                onChange={handlePlanTypeChange}
              >
                <FormControlLabel
                  value={SINGLE_OPTION_PPO}
                  control={<Radio />}
                  label={SINGLE_OPTION_PPO}
                />
                <FormControlLabel
                  value={DUAL_OPTION_PPO}
                  control={<Radio />}
                  label={DUAL_OPTION_PPO}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider" }} mt={5}>
            <Tabs
              variant="fullWidth"
              className="detail-page-tabs"
              value={activeTab}
              onChange={handleChangeTab}
              aria-label="client detail tabs"
            >
              <Tab
                sx={{ maxWidth: "50%" }}
                label={
                  <Typography className={isTabError(0) ? "error" : ""}>
                    {firstTab} <span className={"required"}>*</span>
                  </Typography>
                }
              />
              {coverage.details[PLAN_TYPE] === DUAL_OPTION_PPO && (
                <Tab
                  label={
                    <Typography className={isTabError(1) ? "error" : ""}>
                      {secondTab} <span className={"required"}>*</span>
                    </Typography>
                  }
                />
              )}
            </Tabs>
          </Box>
        </>
      )}
      <TabPanel value={activeTab} index={0}>
        {planForm(!isDHMO ? firstTab : null)}
      </TabPanel>
      {coverage.details[PLAN_TYPE] === DUAL_OPTION_PPO &&
        activeTab === 1 &&
        (coverage.details[DUAL_PLAN_TYPE] === PPO1_PPO2 ||
          coverage.details[DUAL_PLAN_TYPE] === PPO_DHMO) && (
          <TabPanel value={activeTab} index={1}>
            {planForm(!isDHMO ? secondTab : null)}
          </TabPanel>
        )}
    </>
  );
};

export default Dental;
